@if (isSystem) {
  <div class="flex justify-center my-1">
    <span class="badge badge-ghost badge-sm h-auto whitespace-normal py-1 px-2 text-center">{{ message.text }}</span>
  </div>
} @else {
  <div class="chat" [class.chat-end]="message.role === 'user'" [class.chat-start]="message.role !== 'user'">
    <div class="chat-bubble" [ngClass]="bubbleClass">
      @if (message.pending) {
        <span class="loading loading-dots loading-sm" aria-label="Assistent tippt…"></span>
      } @else if (isMarkdown) {
        <div
          class="chat-markdown break-words
                 [&_p]:my-1 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0
                 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1 [&_li]:my-0.5
                 [&_a]:link [&_strong]:font-semibold [&_em]:italic
                 [&_code]:bg-base-content/10 [&_code]:rounded [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.9em]
                 [&_pre]:bg-base-content/10 [&_pre]:rounded-lg [&_pre]:p-2 [&_pre]:overflow-x-auto [&_pre]:my-1 [&_pre_code]:bg-transparent [&_pre_code]:p-0
                 [&_blockquote]:border-l-2 [&_blockquote]:border-current/30 [&_blockquote]:pl-2 [&_blockquote]:italic
                 [&_h1]:text-base [&_h1]:font-semibold [&_h2]:text-base [&_h2]:font-semibold [&_h3]:text-sm [&_h3]:font-semibold"
          [innerHTML]="renderedMarkdown"
        ></div>
      } @else {
        <p class="whitespace-pre-wrap break-words">{{ message.text }}</p>
      }
    </div>

    @if (message.clarify || message.confirm || isCreateRecipeAction || message.recipeJob || recipeSuggestions.length || recipeMatches.length || message.retryable || (debugActive && message.debug)) {
      <!-- `chat-footer` (not a plain div) is load-bearing: daisyUI's `.chat` is a CSS
           grid, and any child here without it falls into auto-placement, which lands
           it in the grid's second column — the one reserved for an avatar image — next
           to the bubble instead of below it. `justify-self-stretch` matters too: the
           grid's own `place-items:start/end` shrink-wraps this item to its content by
           default, so its width stays undefined and text has nothing real to wrap
           against — it either overflows or wraps at an arbitrary narrow point instead
           of using the panel's actual available width. `min-w-0` then lets it shrink
           back down below that stretched width when content (buttons, options) is
           genuinely narrower. -->
      <div
        class="chat-footer flex-col gap-1 min-w-0 max-w-full justify-self-stretch"
        [class.items-end]="message.role === 'user'"
        [class.items-start]="message.role !== 'user'"
      >
        @if (message.clarify) {
          <app-clarify-options
            [clarify]="message.clarify"
            [selectedIndex]="message.selectedOptionIndex"
            (optionSelected)="optionSelected.emit($event)"
          ></app-clarify-options>
        }

        @if (recipeDraftPayload; as draft) {
          <ui-recipe-preview [recipe]="draft"></ui-recipe-preview>
        }

        @if (message.recipeJob; as job) {
          @if (job.status === 'running') {
            <span class="text-sm opacity-70 flex items-center gap-2">
              <span class="loading loading-dots loading-xs"></span>
              Rezept wird erstellt…
            </span>
          } @else if (job.status === 'draft_ready' && job.draft) {
            <ui-recipe-preview [recipe]="job.draft"></ui-recipe-preview>
            <button
              type="button"
              class="btn btn-sm btn-primary gap-1"
              [disabled]="savingRecipeJob"
              (click)="onSaveRecipeJobClick()"
            >
              @if (savingRecipeJob) {
                <span class="loading loading-spinner loading-xs"></span>
              }
              Rezept speichern
            </button>
          } @else if (job.status === 'succeeded') {
            <button type="button" class="btn btn-sm btn-outline gap-1" (click)="openSavedRecipeJob()">
              Rezept öffnen
            </button>
          } @else if (job.status === 'failed') {
            <span class="text-sm text-error">
              Das Rezept konnte nicht erstellt werden{{ job.errorMessage ? ': ' + job.errorMessage : '.' }}
            </span>
          }
        }

        @if (recipeSuggestions.length) {
          <div class="flex flex-col gap-2 w-full">
            @for (idea of recipeSuggestions; track idea.title) {
              <div class="border border-base-300 rounded-lg p-2">
                <div class="font-medium text-sm">{{ idea.title }}</div>
                @if (idea.description) {
                  <div class="text-xs opacity-70 mt-0.5">{{ idea.description }}</div>
                }
                @if (idea.uses_from_stock?.length) {
                  <div class="text-xs opacity-60 mt-1">Nutzt: {{ idea.uses_from_stock!.join(', ') }}</div>
                }
                @if (idea.missing_ingredients?.length) {
                  <div class="text-xs opacity-60">Fehlt noch: {{ idea.missing_ingredients!.join(', ') }}</div>
                }
                <button type="button" class="btn btn-xs btn-primary mt-2" (click)="authorSuggestion.emit(idea)">
                  Rezept erstellen
                </button>
              </div>
            }
          </div>
        }

        @if (recipeMatches.length) {
          <div class="flex flex-col gap-1 w-full">
            @for (r of recipeMatches; track r.recipe_id) {
              <button
                type="button"
                class="border border-base-300 rounded-lg p-2 text-left hover:bg-base-200 flex items-center justify-between gap-2"
                (click)="openRecipeById(r.recipe_id)"
              >
                <span class="font-medium text-sm">{{ r.title }}</span>
                <span class="text-xs opacity-70 shrink-0">
                  @if (r.rating_count > 0) {
                    ★ {{ r.rating_average }} ({{ r.rating_count }})
                  } @else {
                    unbewertet
                  }
                </span>
              </button>
            }
          </div>
        }

        @if (message.confirm) {
          <app-confirm-action
            [answer]="message.confirmAnswer"
            [confirmLabel]="confirmLabel"
            (answered)="confirmAnswered.emit($event)"
          ></app-confirm-action>
        }

        @if (savedRecipeId !== null) {
          <button type="button" class="btn btn-sm btn-outline gap-1" (click)="openRecipe()">
            Rezept öffnen
          </button>
        }

        @if (message.retryable) {
          <button type="button" class="btn btn-xs btn-ghost gap-1" (click)="retry.emit()">
            <fa-icon [icon]="faRotateRight"></fa-icon>
            Erneut senden
          </button>
        }

        @if (debugActive && message.debug; as debug) {
          <!-- `w-fit` + `max-w-[90%]`, not `w-full`: the footer now has real width to
               wrap against (see the note above), but a `w-full` block always starts at
               the footer's left edge — which is correct for assistant messages but pins
               a user message's debug line to the left too, undoing the right alignment
               `items-end` is supposed to give it. `w-fit` shrinks to content (so short
               lines don't force full width and stay correctly end-aligned) while still
               being capped generously enough that only genuinely long values wrap. -->
          <div class="text-[10px] font-mono text-base-content/50 w-fit max-w-[90%]">
            <div class="whitespace-pre-wrap break-words flex items-center gap-1">
              <span>state={{ debug.state }} expects_input={{ debug.expectsInput }} latency={{ debug.latencyMs }}ms</span>
              <button
                type="button"
                class="btn btn-ghost btn-xs btn-square h-4 w-4 min-h-0 p-0 shrink-0"
                title="Request/Response ansehen"
                aria-label="Request/Response ansehen"
                (click)="turnDetailRequested.emit(message)"
              >
                <fa-icon [icon]="faCode" class="text-[9px]"></fa-icon>
              </button>
            </div>
            @if (debug.action || debug.failure || debug.affected) {
              <details>
                <summary class="cursor-pointer select-none w-fit">Details</summary>
                <div class="whitespace-pre-wrap break-words mt-0.5">
                  @if (debug.action; as action) {
                    <div>action={{ action.type }} executed={{ action.executed }} requires_confirmation={{ action.requires_confirmation }}</div>
                  }
                  @if (debug.failure; as failure) {
                    <div>failure={{ failure.reason }}</div>
                  }
                  @if (debug.affected; as affected) {
                    <div class="break-all">affected={{ affected | json }}</div>
                  }
                </div>
              </details>
            }
          </div>
        }
      </div>
    }
  </div>
}
